import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApolloQueryResult, useQuery } from '@apollo/client';
import { default as Form } from '../../components/connectedForm';
import LuckinsItemWorkspace from './LuckinsItemWorkspace';
import LuckinsItem from '../../graphql/entities/product/LuckinsItem';
//Product Range Queries
import { LuckinsSupplier } from '../../graphql/entities';
import LuckinsMajorProductRangeQuery from '../../graphql/queries/product/LuckinsMajorProductRangeQuery.graphql';
import LuckinsMinorProductRangeQuery from '../../graphql/queries/product/LuckinsMinorProductRangeQuery.graphql';
//Commodity Range Queries
import LuckinsCommodity2ListQuery from '../../graphql/queries/product/LuckinsCommodity2ListQuery.graphql';
import LuckinsMajorCommodityRangeQuery from '../../graphql/queries/product/LuckinsMajorCommodityRangeQuery.graphql';
import LuckinsMinorCommodityRangeQuery from '../../graphql/queries/product/LuckinsMinorCommodityRangeQuery.graphql';
import LuckinsSuppliersByCommodityRestriction from '../../graphql/queries/product/LuckinsSuppliersByCommodityRestriction.graphql';
import { useHistory, useLocation } from 'react-router';
import { SecondaryButton } from '../../components/connectedForm/Button';
import {
  FilterOperation,
  IFilterInput,
  ILuckinsCommodityCode2,
  ILuckinsCommodityCode2listQueryResult,
  ILuckinsCommodityCode2listQueryVariables,
  ILuckinsMajorCommodityRangeQueryResult,
  ILuckinsMajorCommodityRangeQueryVariables,
  ILuckinsMajorProductRangeQueryResult,
  ILuckinsMajorProductRangeQueryVariables,
  ILuckinsMinorCommodityRangeQueryResult,
  ILuckinsMinorCommodityRangeQueryVariables,
  ILuckinsMinorProductRangeQueryResult,
  ILuckinsMinorProductRangeQueryVariables,
  ILuckinsSupplier,
  ILuckinsSupplierEdge,
  ILuckinsSuppliersByCommodityRestrictionQueryResult,
  ILuckinsSuppliersByCommodityRestrictionQueryVariables,
  ILuckinsSuppliersQueryResult,
  ILuckinsSuppliersQueryVariables,
  IStringEdge,
} from '../../graphql/schema/p4types';
import { QUERY_PAGE_SIZE } from '../../components/FluentWrapper/useQueryForDetailsList';
import { FluentTable, IFluentTableRowSize } from '../../components/view/FluentViewTable';
import { TableColumnDefinition, UseTableFeaturesOptions } from '@fluentui/react-table';
import { createTableColumn, TableRowId } from '@fluentui/react-components';
import { useCookies } from 'react-cookie';
import { add } from 'date-fns';

const LuckinsFluentTable = ({
  fetchMore,
  itemCount,
  items,
  columns,
  setRemoteFilters,
  setSelectedItems,
}: {
  fetchMore?: Function;
  itemCount: number;
  items: Array<any>;
  columns: Array<TableColumnDefinition<any>>;
  setRemoteFilters: (filters: Array<IFilterInput>) => void;
  setSelectedItems: (selectedItems: Array<any>) => void;
}) => {
  const tableFeatureOptions = useMemo<UseTableFeaturesOptions<any>>(
    () => ({
      columns: columns,
      items: items,
    }),
    [columns, items]
  );

  //Local state for selected options - By id
  const [localSelectedOptionsIds, setLocalSelectedOptionsIds] = useState<Set<TableRowId>>(new Set());

  const loadedPagesRef = useRef<{ [key: number]: boolean }>({ 0: true });
  const isItemLoaded = useCallback((index: number) => {
    return loadedPagesRef.current[(index / QUERY_PAGE_SIZE) >> 0];
  }, []);

  const loadMoreItems: (startIndex: number, stopIndex: number) => Promise<void> | void = (startIndex, stopIndex) => {
    const startPage = (startIndex / QUERY_PAGE_SIZE) >> 0;
    const stopPage = (stopIndex / QUERY_PAGE_SIZE) >> 0;
    const promises: Array<Promise<ApolloQueryResult<any>>> = [];
    for (let i = startPage; i <= stopPage; i++) {
      if (!loadedPagesRef.current[i]) {
        promises.push(
          fetchMore?.({
            variables: {
              first: QUERY_PAGE_SIZE,
              after: i * QUERY_PAGE_SIZE,
            },
          })
        );
        loadedPagesRef.current[i] = true;
      }
    }
    return Promise.all(promises) as unknown as Promise<void>;
  };

  //Maintain a list of all items that we've loaded this session - If
  const [allItemsById, setAllItemsById] = useState<{ [key: TableRowId]: any }>({});
  useEffect(() => {
    setAllItemsById((prev) => {
      const newObj = { ...prev };
      items?.forEach((value) => {
        if (value) {
          newObj[value.id] = value;
        }
      });
      return newObj;
    });
  }, [items]);

  return (
    <FluentTable
      itemCount={itemCount}
      height={246}
      tableFeatureOptions={tableFeatureOptions}
      selectionMode={'multiselect'}
      selectionKey={'id'}
      onSelectionChange={(ev, data) => {
        const selectedItems: Array<any> = [];
        data.selectedItems.forEach((tableRowId) => {
          const selectedItem = allItemsById[tableRowId];
          if (selectedItem) {
            selectedItems.push(selectedItem);
          }
        });

        setLocalSelectedOptionsIds(data.selectedItems);
        setSelectedItems(selectedItems);
      }}
      selectedItems={localSelectedOptionsIds}
      size={IFluentTableRowSize.ExtraSmall}
      infiniteScrollConfig={
        fetchMore
          ? {
              isItemLoaded,
              loadMoreItems,
              setRemoteFilters: (newFilters) => {
                //Reset loaded pages ref when filters change - Loaded set no longer valid.
                loadedPagesRef.current = { 0: true };
                setRemoteFilters(newFilters);
              },
            }
          : undefined
      }
    />
  );
};

//These are actually strings, but we represent them as objects for table display - This is just a wrapper type.
type ILuckinsRange = { id: string; value: string };

const ProductRangeFilter = ({ setWorkspaceFilters }: any) => {
  const [selectedSuppliers, setSelectedSuppliers] = useState<Array<ILuckinsSupplier>>([]);
  const [selectedMajorProductRanges, setSelectedMajorProductRanges] = useState<Array<{ id: string; value: string }>>(
    []
  );
  const [selectedMinorProductRanges, setSelectedMinorProductRanges] = useState<Array<{ id: string; value: string }>>(
    []
  );

  useEffect(() => {
    const supplierFilters: Array<IFilterInput> = selectedSuppliers.map((value: any) => {
      return {
        filterOperation: FilterOperation.Equals,
        filterParameters: {
          field: 'supplierCode',
          value: value.supplierCode,
        },
      };
    });
    const majorProductRangeFilters: Array<IFilterInput> = selectedMajorProductRanges.map((value: any) => {
      return {
        filterOperation: FilterOperation.Equals,
        filterParameters: {
          field: 'productRangeMajor',
          value: value.value,
        },
      };
    });
    const minorProductRangeFilters: Array<IFilterInput> = selectedMinorProductRanges.map((value: any) => {
      return {
        filterOperation: FilterOperation.Equals,
        filterParameters: {
          field: 'productRangeMinor',
          value: value.value,
        },
      };
    });

    setWorkspaceFilters([...supplierFilters, ...majorProductRangeFilters, ...minorProductRangeFilters]);
  }, [selectedSuppliers, selectedMajorProductRanges, selectedMinorProductRanges, setWorkspaceFilters]);

  const [supplierListFilters, setSupplierListFilters] = useState<Array<IFilterInput>>([]);
  const [majorRangeFilters, setMajorRangeFilters] = useState<Array<IFilterInput>>([]);
  const [minorRangeFilters, setMinorRangeFilters] = useState<Array<IFilterInput>>([]);

  const luckinsMajorRangeFilters: Array<IFilterInput> = useMemo(() => {
    return [
      ...majorRangeFilters,
      ...selectedSuppliers.map((value) => {
        return {
          filterOperation: FilterOperation.In,
          filterParameters: {
            field: 'supplierCode',
            value: value.supplierCode,
          },
        };
      }),
    ];
  }, [majorRangeFilters, selectedSuppliers]);
  const luckinsMinorRangeFilters: Array<IFilterInput> = useMemo(() => {
    return [
      ...luckinsMajorRangeFilters,
      ...minorRangeFilters,
      ...selectedMajorProductRanges.map((value) => {
        return {
          filterOperation: FilterOperation.In,
          filterParameters: {
            field: 'productRangeMajor',
            value: value.value,
          },
        };
      }),
    ];
  }, [luckinsMajorRangeFilters, minorRangeFilters, selectedMajorProductRanges]);

  const { data: suppliersData, fetchMore: fetchMoreForSuppliers } = useQuery<
    ILuckinsSuppliersQueryResult,
    ILuckinsSuppliersQueryVariables
  >(LuckinsSupplier.list, {
    variables: { filters: supplierListFilters },
    onCompleted: () => setSelectedMajorProductRanges([]),
  });
  const { data: majorProductRangesData, fetchMore: fetchMoreForMajorProductRanges } = useQuery<
    ILuckinsMajorProductRangeQueryResult,
    ILuckinsMajorProductRangeQueryVariables
  >(LuckinsMajorProductRangeQuery, {
    variables: { filters: luckinsMajorRangeFilters },
    onCompleted: () => setSelectedMinorProductRanges([]),
  });
  const { data: minorProductRangesData, fetchMore: fetchMoreForMinorProductRanges } = useQuery<
    ILuckinsMinorProductRangeQueryResult,
    ILuckinsMinorProductRangeQueryVariables
  >(LuckinsMinorProductRangeQuery, {
    variables: { filters: luckinsMinorRangeFilters },
  });

  const suppliers = suppliersData?.list?.edges?.map((edge) => (edge as ILuckinsSupplierEdge).node) || [];
  const majorRanges = (majorProductRangesData?.list?.edges || []).map((edge) => ({
    id: (edge as IStringEdge).node,
    value: (edge as IStringEdge).node,
  }));
  const minorRanges = (minorProductRangesData?.list?.edges || []).map((edge) => ({
    id: (edge as IStringEdge).node,
    value: (edge as IStringEdge).node,
  }));

  const supplierColumns = useMemo(() => {
    const renderCell = (item: ILuckinsSupplier) => item?.supplierName;
    return [
      createTableColumn<ILuckinsSupplier>({
        columnId: 'supplierName',
        renderCell: renderCell,
      }),
    ];
  }, []);
  const majorRangesColumns = useMemo(() => {
    const renderCell = (item: ILuckinsRange) => item?.value;
    return [
      createTableColumn<ILuckinsRange>({
        columnId: 'value',
        renderCell: renderCell,
      }),
    ];
  }, []);
  const minorRangesColumns = useMemo(() => {
    const renderCell = (item: ILuckinsRange) => item?.value;
    return [
      createTableColumn<ILuckinsRange>({
        columnId: 'value',
        renderCell: renderCell,
      }),
    ];
  }, []);

  return (
    <Form.Row style={{ marginLeft: 0, marginRight: 0 }}>
      <Form.Fieldset cols={1 / 3}>
        <div style={{ height: 300, position: 'relative' }}>
          <LuckinsFluentTable
            fetchMore={fetchMoreForSuppliers}
            items={suppliers}
            columns={supplierColumns}
            itemCount={suppliersData?.list?.count ?? 0}
            setRemoteFilters={setSupplierListFilters}
            setSelectedItems={setSelectedSuppliers}
          />
        </div>
      </Form.Fieldset>
      <Form.Fieldset cols={1 / 3}>
        <div style={{ height: 300, position: 'relative' }}>
          <LuckinsFluentTable
            fetchMore={fetchMoreForMajorProductRanges}
            items={majorRanges}
            columns={majorRangesColumns}
            itemCount={majorProductRangesData?.list?.count ?? 0}
            setRemoteFilters={(filters: Array<IFilterInput>) =>
              setMajorRangeFilters(
                filters.map((filter: IFilterInput) => ({
                  ...filter,
                  filterParameters: { field: 'productRangeMajor', value: filter.filterParameters?.value },
                }))
              )
            }
            setSelectedItems={setSelectedMajorProductRanges}
          />
        </div>
      </Form.Fieldset>
      <Form.Fieldset cols={1 / 3}>
        <div style={{ height: 300, position: 'relative' }}>
          <LuckinsFluentTable
            fetchMore={fetchMoreForMinorProductRanges}
            items={minorRanges}
            columns={minorRangesColumns}
            itemCount={minorProductRangesData?.list?.count ?? 0}
            setRemoteFilters={(filters: Array<IFilterInput>) =>
              setMinorRangeFilters(
                filters.map((filter: IFilterInput) => ({
                  ...filter,
                  filterParameters: { field: 'productRangeMinor', value: filter.filterParameters?.value },
                }))
              )
            }
            setSelectedItems={setSelectedMinorProductRanges}
          />
        </div>
      </Form.Fieldset>
    </Form.Row>
  );
};

const CommodityRangeFilters = ({ setWorkspaceFilters }: any) => {
  const [selectedCommodity2Ranges, setSelectedCommodity2Ranges] = useState<Array<ILuckinsCommodityCode2>>([]);
  const [selectedMajorCommodityRanges, setSelectedMajorCommodityRanges] = useState<
    Array<{ id: string; value: string }>
  >([]);
  const [selectedMinorCommodityRanges, setSelectedMinorCommodityRanges] = useState<
    Array<{ id: string; value: string }>
  >([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState<Array<any>>([]);

  useEffect(() => {
    const commodity2Filters = selectedCommodity2Ranges.map((value) => {
      return {
        filterOperation: 'STARTS_WITH',
        filterParameters: {
          field: 'commodityCode2',
          value: value.commodityCode!.substring(0, 3),
        },
      };
    });
    const majorCommodityRangeFilters = selectedMajorCommodityRanges.map((value) => {
      return {
        filterOperation: 'EQUALS',
        filterParameters: {
          field: 'commodityMajor',
          value: value.value,
        },
      };
    });
    const minorCommodityRangeFilters = selectedMinorCommodityRanges.map((value) => {
      return {
        filterOperation: 'EQUALS',
        filterParameters: {
          field: 'commodityMinor',
          value: value.value,
        },
      };
    });
    const supplierFilters = selectedSuppliers.map((value) => {
      return {
        filterOperation: 'EQUALS',
        filterParameters: {
          field: 'supplierCode',
          value: value.supplierCode,
        },
      };
    });

    setWorkspaceFilters([
      ...commodity2Filters,
      ...majorCommodityRangeFilters,
      ...minorCommodityRangeFilters,
      ...supplierFilters,
    ]);
  }, [
    selectedCommodity2Ranges,
    selectedMajorCommodityRanges,
    selectedMinorCommodityRanges,
    selectedSuppliers,
    setWorkspaceFilters,
  ]);

  const [commodity2Filters, setCommodity2Filters] = useState<Array<IFilterInput>>([]);
  const [majorCommodityFilters, setMajorCommodityFilters] = useState<Array<IFilterInput>>([]);
  const [minorCommodityFilters, setMinorCommodityFilters] = useState<Array<IFilterInput>>([]);

  const luckinsMajorCommodityFilters: Array<IFilterInput> = useMemo(() => {
    return [
      ...majorCommodityFilters,
      ...selectedCommodity2Ranges.map((value) => {
        //TODO - Convert to nested filters with 'startsWith' (Requires changes in P4-6987) - Currently hacked to work service-side.
        return {
          filterOperation: FilterOperation.In,
          filterParameters: {
            field: 'commodityCode2',
            value: value.commodityCode?.substring(0, 3),
          },
        };
      }),
    ];
  }, [majorCommodityFilters, selectedCommodity2Ranges]);
  const luckinsMinorCommodityFilters: Array<IFilterInput> = useMemo(() => {
    return [
      ...minorCommodityFilters,
      ...selectedMajorCommodityRanges.map((value) => {
        return {
          filterOperation: FilterOperation.In,
          filterParameters: {
            field: 'commodityMajor',
            value: value.value,
          },
        };
      }),
    ];
  }, [minorCommodityFilters, selectedMajorCommodityRanges]);

  const { data: commodity2RangesData, fetchMore: fetchMoreForCommodity2 } = useQuery<
    ILuckinsCommodityCode2listQueryResult,
    ILuckinsCommodityCode2listQueryVariables
  >(LuckinsCommodity2ListQuery, {
    variables: {
      filters: commodity2Filters,
    },
    onCompleted: () => setSelectedMajorCommodityRanges([]),
  });
  const { data: majorCommodityRangesData, fetchMore: fetchMoreForMajorRange } = useQuery<
    ILuckinsMajorCommodityRangeQueryResult,
    ILuckinsMajorCommodityRangeQueryVariables
  >(LuckinsMajorCommodityRangeQuery, {
    variables: { filters: luckinsMajorCommodityFilters },
    onCompleted: () => setSelectedMinorCommodityRanges([]),
  });

  const { data: minorCommodityRangesData, fetchMore: fetchMoreForMinorRange } = useQuery<
    ILuckinsMinorCommodityRangeQueryResult,
    ILuckinsMinorCommodityRangeQueryVariables
  >(LuckinsMinorCommodityRangeQuery, {
    variables: { filters: luckinsMinorCommodityFilters },
    onCompleted: () => setSelectedSuppliers([]),
  });

  //TODO - Needs paging added - Skipping over as it requires a hack like commodity major, but expecting relatively low usage and think we can wait.
  //     - Once P4-6987 is merged, we can convert this to use nested filtering and just use LuckinsSupplier.list as the driving query.
  const { data: suppliersData } = useQuery<
    ILuckinsSuppliersByCommodityRestrictionQueryResult,
    ILuckinsSuppliersByCommodityRestrictionQueryVariables
  >(LuckinsSuppliersByCommodityRestriction, {
    variables: {
      majorCommodityRanges: selectedMajorCommodityRanges.map((entry) => entry.value),
      minorCommodityRanges: selectedMinorCommodityRanges.map((entry) => entry.value),
    },
    skip: !selectedMinorCommodityRanges || selectedMinorCommodityRanges.length === 0,
  });

  const commodity2Ranges = commodity2RangesData?.list?.edges?.map((edge) => edge?.node) || [];
  const majorRanges = (majorCommodityRangesData?.list?.edges || []).map((edge) => ({
    id: (edge as IStringEdge).node,
    value: (edge as IStringEdge).node,
  }));
  const minorRanges = (minorCommodityRangesData?.list?.edges || []).map((edge) => ({
    id: (edge as IStringEdge).node,
    value: (edge as IStringEdge).node,
  }));
  const suppliers = suppliersData?.list || [];

  const commodity2Columns = useMemo(() => {
    const renderCell = (item: ILuckinsCommodityCode2) => item?.description;
    return [
      createTableColumn<ILuckinsCommodityCode2>({
        columnId: 'description',
        renderCell: renderCell,
      }),
    ];
  }, []);

  const majorRangesColumns = useMemo(() => {
    const renderCell = (item: ILuckinsRange) => item?.value;
    return [
      createTableColumn<ILuckinsRange>({
        columnId: 'value',
        renderCell: renderCell,
      }),
    ];
  }, []);

  const minorRangesColumns = useMemo(() => {
    const renderCell = (item: ILuckinsRange) => item?.value;
    return [
      createTableColumn<ILuckinsRange>({
        columnId: 'value',
        renderCell: renderCell,
      }),
    ];
  }, []);

  const supplierColumns = useMemo(() => {
    const renderCell = (item: ILuckinsSupplier) => item?.supplierName;
    return [
      createTableColumn<ILuckinsSupplier>({
        columnId: 'supplierName',
        renderCell: renderCell,
      }),
    ];
  }, []);

  return (
    <Form.Row style={{ marginLeft: 0, marginRight: 0 }}>
      <Form.Fieldset cols={1 / 4}>
        <div style={{ height: 300, position: 'relative' }}>
          <LuckinsFluentTable
            fetchMore={fetchMoreForCommodity2}
            items={commodity2Ranges}
            columns={commodity2Columns}
            itemCount={commodity2RangesData?.list?.count ?? 0}
            setRemoteFilters={setCommodity2Filters}
            setSelectedItems={setSelectedCommodity2Ranges}
          />
        </div>
      </Form.Fieldset>
      <Form.Fieldset cols={1 / 4}>
        <div style={{ height: 300, position: 'relative' }}>
          <LuckinsFluentTable
            fetchMore={fetchMoreForMajorRange}
            items={majorRanges}
            columns={majorRangesColumns}
            itemCount={majorCommodityRangesData?.list?.count ?? 0}
            setRemoteFilters={(filters: Array<IFilterInput>) =>
              setMajorCommodityFilters(
                filters.map((filter: IFilterInput) => ({
                  ...filter,
                  filterParameters: { field: 'commodityMajor', value: filter.filterParameters?.value },
                }))
              )
            }
            setSelectedItems={setSelectedMajorCommodityRanges}
          />
        </div>
      </Form.Fieldset>
      <Form.Fieldset cols={1 / 4}>
        <div style={{ height: 300, position: 'relative' }}>
          <LuckinsFluentTable
            fetchMore={fetchMoreForMinorRange}
            items={minorRanges}
            columns={minorRangesColumns}
            itemCount={minorCommodityRangesData?.list?.count ?? 0}
            setRemoteFilters={(filters: Array<IFilterInput>) =>
              setMinorCommodityFilters(
                filters.map((filter: IFilterInput) => ({
                  ...filter,
                  filterParameters: { field: 'commodityMinor', value: filter.filterParameters?.value },
                }))
              )
            }
            setSelectedItems={setSelectedMinorCommodityRanges}
          />
        </div>
      </Form.Fieldset>
      <Form.Fieldset cols={1 / 4}>
        <div style={{ height: 300, position: 'relative' }}>
          <LuckinsFluentTable
            items={suppliers}
            columns={supplierColumns}
            itemCount={suppliers.length}
            setRemoteFilters={() => {
              /*Not currently supported*/
            }}
            setSelectedItems={setSelectedSuppliers}
          />
        </div>
      </Form.Fieldset>
    </Form.Row>
  );
};

const PRODUCT_RANGE = 'PRODUCT_RANGE';
const COMMODITY_RANGE = 'COMMODITY_RANGE';

const FILTER_TOGGLE_COOKIE = 'FILTER_TOGGLE_COOKIE';

const LuckinsEnquiryView = (props: any) => {
  const history = useHistory();
  const location = useLocation();
  const [cookie, setCookie] = useCookies([FILTER_TOGGLE_COOKIE]);
  const [workspaceFilters, setWorkspaceFilters] = useState([]);

  const onRowClick = (rowData: any, rowIdx: number) => {
    if (props.onRowClick) {
      props.onRowClick(rowData, rowIdx);
      return;
    }
    //Open Luckins item View.
    history.push({
      pathname: LuckinsItem.formUrl + rowData.id,
      state: {
        previousLocation: location.pathname || 'back',
      },
    });
  };

  const toggleFilterType = {
    description: `Toggle Filter (${
      cookie[FILTER_TOGGLE_COOKIE] === COMMODITY_RANGE ? 'Product Range' : 'Commodity Code'
    })`,
    individualButton: true,
    component: SecondaryButton,
    fn: () =>
      cookie[FILTER_TOGGLE_COOKIE] === COMMODITY_RANGE
        ? setCookie(FILTER_TOGGLE_COOKIE, PRODUCT_RANGE, {
            expires: add(new Date(), { years: 1 }),
          })
        : setCookie(FILTER_TOGGLE_COOKIE, COMMODITY_RANGE, {
            expires: add(new Date(), { years: 1 }),
          }),
    ignorePermissions: true,
  };

  const luckinsEnquiryHeader = (
    <>
      {props.embeddedView ? null : (
        <Form.HeaderBlock
          name={'Luckins Enquiry'}
          onCancel={history.goBack}
          noDeleteOption
          noSaveOption
          actionButtons={[toggleFilterType]}
        />
      )}
      {cookie[FILTER_TOGGLE_COOKIE] === PRODUCT_RANGE ? (
        <ProductRangeFilter setWorkspaceFilters={setWorkspaceFilters} />
      ) : (
        <CommodityRangeFilters setWorkspaceFilters={setWorkspaceFilters} />
      )}
    </>
  );

  return (
    <LuckinsItemWorkspace
      {...props}
      embeddedView={true}
      predefinedFilters={workspaceFilters}
      viewScreenName={props.viewScreenName}
      entity={LuckinsItem}
      onRowClick={onRowClick}
      noForm={true}
      contentHeight={Math.max(450, window.innerHeight - 528)}
      customHeader={luckinsEnquiryHeader}
      ignoreContainerWidth={true}
      {...props.workspaceProps}
    />
  );
};

export default LuckinsEnquiryView;
